import { useForm } from '@tanstack/react-form';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useHistory } from 'react-router-dom';
import routes from '~/routes';
import type { AreaSelection, DiscountRateScenarioFormData } from './types';
import {
  enumsLoadableAtom,
  saveScenarioLoadableAtom,
  toastAtom,
} from './atoms';
import {
  buildCreatePayload,
  buildUpdatePayload,
  DEFAULT_AREA_SELECTION,
  getAreaDisplayName,
  GLOBAL_SELECTION_ID,
  hasDuplicateRiskingEntries,
  isAreaSelectionComplete,
  isRiskingEntryValid,
  nextId,
  type FormMode,
} from './utils';
import {
  categoryLabels,
  hurdleConflictMessage,
  isDiscountRateLineValid,
  overlappingCombinationMessage,
  type CategoryLabels,
} from './discount-rate-line-utils';
import { getMemoizedOverlappingAreaIds } from 'utils/geometry-overlap';

// ─────────────────────────────────────
// Context type
// ─────────────────────────────────────

interface ScenarioFormContextType {
  form: ReturnType<typeof useCreateForm>;
  mode: FormMode;
  selectedId: string | number;
  setSelectedId: (id: string | number) => void;
  addArea: () => void;
  removeArea: (id: number | string) => void;
  handleSave: () => void;
  isSaving: boolean;
}

const ScenarioFormContext = createContext<ScenarioFormContextType | null>(null);

// ─────────────────────────────────────
// Provider
// ─────────────────────────────────────

interface ScenarioFormProviderProps {
  initialValues: DiscountRateScenarioFormData;
  mode: FormMode;
  scenarioId?: string;
  geometries: Record<string, GeoJSON.Geometry>;
  children: ReactNode;
}

const useCreateForm = (
  defaultValues: DiscountRateScenarioFormData,
  geometries: Record<string, GeoJSON.Geometry>,
  labels: CategoryLabels
) => {
  return useForm({
    defaultValues,
    validators: {
      onChange: ({ value }: { value: DiscountRateScenarioFormData }) => {
        const hasIncompleteArea = value.areas.some(
          (a) => !isAreaSelectionComplete(a)
        );
        if (hasIncompleteArea) return 'All areas must have a selection';

        const ids = getMemoizedOverlappingAreaIds(value.areas, geometries);
        if (ids.size > 0)
          return 'One or more areas have overlapping geometries';

        for (const area of value.areas) {
          const hasInvalidRate = area.discountRateLines.some(
            (line) => !isDiscountRateLineValid(line)
          );
          if (hasInvalidRate)
            return 'One or more discount rate lines are incomplete';

          // The editor shows one area at a time, so these name the area to open.
          const overlap = overlappingCombinationMessage(
            area.discountRateLines,
            labels
          );
          if (overlap) return `${getAreaDisplayName(area)}: ${overlap}`;

          const hurdleConflict = hurdleConflictMessage(
            area.discountRateLines,
            labels
          );
          if (hurdleConflict)
            return `${getAreaDisplayName(area)}: ${hurdleConflict}`;

          const hasInvalidRisk = area.riskingEntries.some(
            (e) => !isRiskingEntryValid(e)
          );
          if (hasInvalidRisk)
            return 'One or more risking entries are incomplete';

          if (hasDuplicateRiskingEntries(area.riskingEntries)) {
            return 'Duplicate risking entries detected';
          }
        }

        return undefined;
      },
    },
  });
};

/**
 * Provides form state management for the discount rate scenario form.
 * @param {ScenarioFormProviderProps} props - The provider props
 * @return {JSX.Element} The provider component
 */
export function ScenarioFormProvider({
  initialValues,
  mode,
  scenarioId,
  geometries,
  children,
}: ScenarioFormProviderProps): JSX.Element {
  const enumsState = useAtomValue(enumsLoadableAtom);
  const enums = enumsState.state === 'hasData' ? enumsState.data : null;
  const labels = useMemo(() => categoryLabels(enums), [enums]);
  const form = useCreateForm(initialValues, geometries, labels);

  // Save-blocking messages name categories by their enum labels; refresh them once loaded.
  useEffect(() => {
    if (enums) void form.validate('change');
  }, [enums, form]);
  const [selectedId, setSelectedId] = useState<string | number>(
    GLOBAL_SELECTION_ID
  );
  const [saveState, triggerSave] = useAtom(saveScenarioLoadableAtom);
  const removedAreaIdsRef = useRef<number[]>([]);
  const history = useHistory();

  const isSaving = saveState.state === 'loading';
  const setToast = useSetAtom(toastAtom);

  /**
   * Adds a new area with default values.
   */
  const addArea = useCallback(() => {
    const id = nextId();
    const currentAreas = form.getFieldValue('areas');
    form.setFieldValue('areas', [
      ...currentAreas,
      { ...DEFAULT_AREA_SELECTION, id },
    ]);
    setSelectedId(id);
  }, [form]);

  /**
   * Removes an area by ID. Tracks backend IDs for _destroy on update.
   * @param {number | string} id - The area ID to remove
   */
  const removeArea = useCallback(
    (id: number | string) => {
      const currentAreas = form.getFieldValue('areas');
      const area = currentAreas.find((a: AreaSelection) => a.id === id);
      if (area?.status === 'saved') {
        removedAreaIdsRef.current.push(area.id);
      }
      form.setFieldValue(
        'areas',
        currentAreas.filter((a: AreaSelection) => a.id !== id)
      );

      if (selectedId === id) {
        setSelectedId(GLOBAL_SELECTION_ID);
      }
    },
    [form, selectedId]
  );

  /**
   * Saves the scenario to the backend.
   */
  const handleSave = useCallback(async () => {
    const values = form.state.values;
    setToast(null);

    if (mode === 'create') {
      const payload = buildCreatePayload(values);
      try {
        const response = await triggerSave({
          mode: 'create',
          payload,
        });
        setToast({ message: 'Scenario saved', type: 'success' });
        history.replace(
          routes.discountRateScenarioDetailPath.replace(
            ':id',
            String(response.id)
          )
        );
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to save scenario';
        setToast({ message, type: 'error' });
      }
    } else {
      const payload = buildUpdatePayload(values, removedAreaIdsRef.current);
      try {
        await triggerSave({
          mode: 'edit',
          id: Number(scenarioId),
          payload,
        });
        setToast({ message: 'Scenario saved', type: 'success' });
        window.location.reload();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to save scenario';
        setToast({ message, type: 'error' });
      }
    }
  }, [form, mode, scenarioId, triggerSave, history, setToast]);

  const value = useMemo(
    () => ({
      form,
      mode,
      selectedId,
      setSelectedId,
      addArea,
      removeArea,
      handleSave,
      isSaving,
    }),
    [form, mode, selectedId, addArea, removeArea, handleSave, isSaving]
  );

  return (
    <ScenarioFormContext.Provider value={value}>
      {children}
    </ScenarioFormContext.Provider>
  );
}

// ─────────────────────────────────────
// Hook
// ─────────────────────────────────────

/**
 * Returns the scenario form context. Must be used within a ScenarioFormProvider.
 * @return {ScenarioFormContextType} The scenario form context
 * @throws {Error} If used outside of ScenarioFormProvider
 */
export function useScenarioForm(): ScenarioFormContextType {
  const context = useContext(ScenarioFormContext);
  if (context === null) {
    throw new Error(
      'useScenarioForm must be used within a ScenarioFormProvider'
    );
  }
  return context;
}
